import i18next from 'i18next';
import { useSyncExternalStore } from 'react';
import { useAppUserDefaultStore } from '../../../stores/appUserDefaultStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { useTimeIntervalStore } from '../../../stores/appTimeIntervalStore';
import { RestaurantSectionType } from '../../../types/RestaurantSectionType';
import { ensurePracticeRestaurant } from './practiceSession';
import { buildPracticeDish, buildPracticeExampleDishes, buildPracticeOrder, buildPracticeRevenue } from './costPriceFixture';
import { beginPracticeData, endPracticeData, isPracticeId, PRACTICE_ID_PREFIX, practiceDishes } from './practiceGuards';

export const UTILIZATION_LINK_DISH_ID = `${PRACTICE_ID_PREFIX}lambStarter-dish`;
let active = false;
let fixture = false;
let customer = false;
let startedWithCustomer = false;
let customerTime: NonNullable<typeof snapshot>['time'] | null = null;
let revision = 0;
let session = 0;
const listeners = new Set<() => void>();
const emit = () => { revision++; listeners.forEach(fn => fn()); };
export const subscribeUtilizationPractice = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
export const utilizationPracticeRevision = () => revision;
export const utilizationPracticeSession = () => session;
export const notifyUtilizationValues = () => { if (active) emit(); };
export const isUtilizationPractice = () => active;
export const isUtilizationFixture = () => active && fixture;
export function useUtilizationPractice() {
  useSyncExternalStore(subscribeUtilizationPractice, utilizationPracticeRevision, utilizationPracticeRevision);
  return { active, fixture, customer, session, originalTime: snapshot?.time, originalOverallType: snapshot?.overallTypeFilter };
}
let snapshot: { overallTypeFilter: ReturnType<typeof useFiltersStore.getState>['overallTypeFilter']; supplierFilters: string[]; categoryFilters: string[]; time: Pick<ReturnType<typeof useTimeIntervalStore.getState>, 'view' | 'cursor' | 'startDate' | 'endDate' | 'customStartDate' | 'customEndDate'> } | null = null;

export function startUtilizationPractice(options?: { source: 'customer' | 'fixture'; allTime?: boolean }): boolean {
  if (!ensurePracticeRestaurant()) return false;
  if (!snapshot) {
    const { view, cursor, startDate, endDate, customStartDate, customEndDate } = useTimeIntervalStore.getState();
    snapshot = { overallTypeFilter: useFiltersStore.getState().overallTypeFilter,
      supplierFilters: useCostPriceStore.getState().addIngredientSupplierFilters,
      categoryFilters: useCostPriceStore.getState().addIngredientCategoryFilters,
      time: { view, cursor, startDate, endDate, customStartDate, customEndDate } };
  }
  if (active) { endPracticeData(); clearPracticeRows(); }
  active = true;
  fixture = false;
  customer = false;
  session++;
  useTimeIntervalStore.setState(snapshot.time);
  useFiltersStore.setState({ overallTypeFilter: 'all' });
  useCostPriceStore.setState({ addIngredientSupplierFilters: [], addIngredientCategoryFilters: [] });
  customer = options?.source === 'customer';
  startedWithCustomer = customer;
  if (options?.allTime) useTimeIntervalStore.getState().setView('all');
  const { view, cursor, startDate, endDate, customStartDate, customEndDate } = useTimeIntervalStore.getState();
  customerTime = { view, cursor, startDate, endDate, customStartDate, customEndDate };
  if (options?.source === 'fixture') activateUtilizationFixture();
  emit();
  return true;
}

export function activateUtilizationFixture(): void {
  if (!active || fixture) return;
  const restaurantId = useAppUserDefaultStore.getState().restaurant?.id;
  if (!restaurantId) return;
  const dish = buildPracticeDish(restaurantId, true);
  // The existing connection starts at 5 g. A second lamb dish contributes
  // 35.2 kg, giving 35.4 kg expected usage against 40 kg ordered.
  dish.ingredients = dish.ingredients.filter(i => i.articleId !== `${PRACTICE_ID_PREFIX}garlic`).map(i => i.articleId === `${PRACTICE_ID_PREFIX}lamb` ? { ...i, quantityNeeded: 5 } : i);
  const examples = buildPracticeExampleDishes(restaurantId, true).map(d => ({ ...d,
    ingredients: d.ingredients.filter(i => i.articleId !== `${PRACTICE_ID_PREFIX}garlic`) }));
  const secondDish = { ...buildPracticeDish(restaurantId, true),
    id: `${PRACTICE_ID_PREFIX}lamb-main`,
    costPriceName: i18next.t('onboarding.utilization.practice.secondDish'),
    menuItemName: i18next.t('onboarding.utilization.practice.secondDish'),
  };
  secondDish.ingredients = secondDish.ingredients.filter(i => i.articleId !== `${PRACTICE_ID_PREFIX}garlic`);
  examples.push(secondDish);
  const order = buildPracticeOrder(restaurantId);
  order.restaurantSection = {
    id: `${PRACTICE_ID_PREFIX}food-section`, restaurantId, name: order.clientName,
    createdAt: order.orderDate.toISOString(), updatedAt: order.orderDate.toISOString(),
    restaurantSectionType: RestaurantSectionType.FOOD, combinedOrderingAccount: false,
  };
  order.productData = order.productData.map(p => p.articleId === `${PRACTICE_ID_PREFIX}lamb` ? { ...p, quantity: 20, rawQuantity: 20 } : p);
  order.total = order.productData.reduce((sum, p) => sum + p.quantity * p.price, 0);
  order.totalWithTaxes = order.productData.reduce((sum, p) => sum + p.quantity * (p.priceWithTaxes ?? p.price), 0);
  const revenue = buildPracticeRevenue(restaurantId, [dish, ...examples]);
  for (const category of revenue[0].revenueData.categories) {
    category.items = category.items.map(item => item.name === secondDish.menuItemName ? { ...item, quantity: 176, price: 176 * 28, exVat: Number((176 * 28 / 1.09).toFixed(2)) } : item);
    category.quantity = category.items.reduce((sum, item) => sum + item.quantity, 0);
    category.price = category.items.reduce((sum, item) => sum + item.price, 0);
    category.exVat = category.items.reduce((sum, item) => sum + (item.exVat ?? 0), 0);
  }
  beginPracticeData(restaurantId, dish, examples, 1, { orderHistoryList: [order], revenueHistoryList: revenue });
  fixture = true;
  customer = false;
  useTimeIntervalStore.getState().setView('week');
  emit();
}

export function setUtilizationExerciseSource(exercise: boolean): void {
  if (!active || !startedWithCustomer) return;
  if (exercise) activateUtilizationFixture();
  else if (fixture) {
    endPracticeData();
    clearPracticeRows();
    fixture = false;
    customer = true;
    if (customerTime) useTimeIntervalStore.setState(customerTime);
    emit();
  }
}

export function utilizationLinkDish() { return practiceDishes().find(d => d.id === UTILIZATION_LINK_DISH_ID); }

function clearPracticeRows() {
  // Editor updates may upsert fixture rows. Remove by reserved ID only: never
  // restore a stale customer-data snapshot over a concurrent refetch.
  useCostPriceStore.setState(s => ({ costPriceList: s.costPriceList.filter(d => !isPracticeId(d.id)) }));
  useSubrecipesStore.setState(s => ({ subrecipesList: s.subrecipesList.filter(d => !isPracticeId(d.id)),
    subrecipesById: new Map(Array.from(s.subrecipesById).filter(([id]) => !isPracticeId(id))) }));
}

export function endUtilizationPractice(): void {
  if (!active) return;
  active = false;
  fixture = false;
  customer = false;
  startedWithCustomer = false;
  customerTime = null;
  endPracticeData();
  clearPracticeRows();
  if (snapshot) {
    useFiltersStore.setState({ overallTypeFilter: snapshot.overallTypeFilter });
    useTimeIntervalStore.setState(snapshot.time);
    useCostPriceStore.setState({ addIngredientSupplierFilters: snapshot.supplierFilters, addIngredientCategoryFilters: snapshot.categoryFilters });
  }
  snapshot = null;
  emit();
}
